/**
 * Location data from country-state-city (NPM), loaded on demand.
 * Replaces the legacy cityStatecodes.json (~125K records).
 */

import type {ICity, ICountry, IState} from 'country-state-city';

export type {ICity, ICountry, IState};

type CscModule = typeof import('country-state-city');

let cscPromise: Promise<CscModule> | null = null;

function loadCsc(): Promise<CscModule> {
  if (!cscPromise) {
    cscPromise = import('country-state-city');
  }
  return cscPromise;
}

export async function getAllCountries(): Promise<ICountry[]> {
  const {Country} = await loadCsc();
  return Country.getAllCountries().sort((a, b) => a.name.localeCompare(b.name));
}

export async function getStatesForCountry(countryCode: string): Promise<IState[]> {
  if (!countryCode) return [];
  const {State} = await loadCsc();
  return State.getStatesOfCountry(countryCode).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export async function getCitiesForState(
  countryCode: string,
  stateCode: string,
): Promise<ICity[]> {
  if (!countryCode || !stateCode) return [];
  const {City} = await loadCsc();
  return City.getCitiesOfState(countryCode, stateCode).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}

export async function findCountryByName(name: string): Promise<ICountry | undefined> {
  const trimmed = name?.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  const countries = await getAllCountries();
  return countries.find(
    (c) =>
      c.name.toLowerCase() === lower || c.isoCode.toLowerCase() === lower,
  );
}

export async function findStateByName(
  countryCode: string,
  name: string,
): Promise<IState | undefined> {
  const trimmed = name?.trim();
  if (!trimmed || !countryCode) return undefined;
  const lower = trimmed.toLowerCase();
  const states = await getStatesForCountry(countryCode);
  return states.find(
    (s) =>
      s.name.toLowerCase() === lower || s.isoCode.toLowerCase() === lower,
  );
}

export async function resolveLocationCodes(
  countryName: string,
  stateName: string,
): Promise<{countryCode: string; stateCode: string}> {
  const country = await findCountryByName(countryName);
  if (!country) return {countryCode: '', stateCode: ''};
  const state = await findStateByName(country.isoCode, stateName);
  return {
    countryCode: country.isoCode,
    stateCode: state?.isoCode ?? '',
  };
}
